import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Cookie, ExternalLink } from "lucide-react";

const LAST_UPDATED = "2 April 2026";

export const CookiePolicy = () => {
  const reopenBanner = () => {
    try {
      localStorage.removeItem("cookie-consent-v1");
    } catch (err) {
      console.warn("CookiePolicy: localStorage unavailable", err);
    }
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gradient-to-br from-teal-700 to-teal-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-teal-100 hover:text-white transition-colors mb-4 text-sm"
            data-testid="cookie-back-home"
          >
            <ArrowLeft className="h-4 w-4" /> Back to website
          </Link>
          <div className="flex items-center gap-3 mb-3">
            <Cookie className="h-8 w-8 text-orange-400" />
            <h1 className="text-3xl md:text-4xl font-bold">Cookie Policy</h1>
          </div>
          <p className="text-teal-100">
            What cookies we use on barnstoncourt.com, why, and how you can control them.
          </p>
          <p className="text-teal-200 text-sm mt-3">Last updated: {LAST_UPDATED}</p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 prose prose-teal max-w-none">
        <section>
          <p>
            Cookies are small text files which a website may put on your computer or
            mobile device when you first visit the website. The cookies will help the
            website recognise your device the next time you visit. Web beacons or other
            similar files can also do the same thing. We use the term &lsquo;cookies&rsquo;
            in this policy to refer to all files that collect information in this way.
          </p>
          <p>
            We use cookies to distinguish you from other users of the Website. This helps
            us to provide you with a good experience when you use the Website and also
            allows us to improve the services we provide to you.
          </p>
        </section>

        <h2>Categories of cookies we use</h2>

        <h3>1. Strictly necessary cookies</h3>
        <p>
          These cookies are essential to enable you to move around the Website and use
          its features (for example, remembering your cookie-preference choice so we do
          not show you the banner on every page). Disabling them may mean parts of the
          Website do not work correctly. <strong>Consent is not required for these
          cookies and they cannot be turned off.</strong>
        </p>

        <h3>2. Analytical / performance cookies</h3>
        <p>
          We use these cookies to collect information about how visitors use the Website
          &mdash; for instance, which pages visitors go to most, the order they visit
          pages in, errors encountered, and how long visitors stay. This helps us improve
          the way the Website works, for example by ensuring that visitors are finding
          what they are looking for easily. These cookies <strong>require your consent</strong>{" "}
          and you can withdraw consent at any time.
        </p>

        <h3>3. Functionality cookies</h3>
        <p>
          We do <strong>not</strong> currently use functionality cookies (e.g.
          remembering text size, fonts or other preferences).
        </p>

        <h3>4. Targeting / advertising cookies</h3>
        <p>
          We do <strong>not</strong> currently use targeting or advertising cookies, and
          we do not share data with advertising networks.
        </p>

        <h2>Specific cookies used on this Website</h2>

        <h3>Part 1 &mdash; Cookies set directly by us</h3>
        <div className="not-prose overflow-x-auto my-4">
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-teal-50 text-teal-900">
              <tr>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Cookie</th>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Category</th>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Purpose</th>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Expiry</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr>
                <td className="px-4 py-3 border-b border-gray-100 font-mono text-xs">cookie-consent-v1</td>
                <td className="px-4 py-3 border-b border-gray-100">Strictly necessary</td>
                <td className="px-4 py-3 border-b border-gray-100">Stores your cookie-consent choice so the banner does not reappear on every visit.</td>
                <td className="px-4 py-3 border-b border-gray-100">12 months</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3>Part 2 &mdash; Third-party cookies</h3>
        <p>
          Third parties (such as analytics providers and embedded services) may also set
          cookies on the Website, which we do not control directly:
        </p>
        <div className="not-prose overflow-x-auto my-4">
          <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
            <thead className="bg-teal-50 text-teal-900">
              <tr>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Third party</th>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Category</th>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">Purpose</th>
                <th className="px-4 py-2 text-left font-semibold border-b border-gray-200">More info</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              <tr>
                <td className="px-4 py-3 border-b border-gray-100 font-semibold">PostHog</td>
                <td className="px-4 py-3 border-b border-gray-100">Analytical</td>
                <td className="px-4 py-3 border-b border-gray-100">Helps us understand which pages, features and content visitors interact with so we can improve the Website. We only enable PostHog after you accept analytics cookies.</td>
                <td className="px-4 py-3 border-b border-gray-100">
                  <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
                    posthog.com/privacy <ExternalLink className="inline h-3 w-3" />
                  </a>
                </td>
              </tr>
              <tr>
                <td className="px-4 py-3 border-b border-gray-100 font-semibold">Google Maps</td>
                <td className="px-4 py-3 border-b border-gray-100">Functional (embedded service)</td>
                <td className="px-4 py-3 border-b border-gray-100">Powers the embedded map on our Visit Us section. Google may set cookies when you interact with the map.</td>
                <td className="px-4 py-3 border-b border-gray-100">
                  <a href="https://policies.google.com/technologies/cookies" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
                    Google cookies <ExternalLink className="inline h-3 w-3" />
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h2>Your choices &amp; how to manage cookies</h2>
        <p>
          When you first visit the Website, you will see a cookie banner asking whether
          you accept non-essential (analytical) cookies. You can:
        </p>
        <ul>
          <li><strong>Accept</strong> &mdash; enables analytical cookies (PostHog).</li>
          <li><strong>Reject</strong> &mdash; only strictly-necessary cookies are set; no analytics will be loaded.</li>
        </ul>
        <p>You can change your decision at any time by clicking the button below:</p>
        <div className="not-prose my-4">
          <button
            onClick={reopenBanner}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-5 py-2.5 rounded-lg transition-colors"
            data-testid="cookie-policy-reopen-banner"
          >
            <Cookie className="h-4 w-4" /> Update cookie preferences
          </button>
        </div>

        <p>
          Most browsers also accept cookies automatically but allow you to change your
          cookie preferences by adjusting your browser settings to refuse the setting of
          all or some cookies. You can usually do this by visiting the &lsquo;options&rsquo;
          or &lsquo;preferences&rsquo; menu on your browser. Please note that if you
          choose to block all cookies (including strictly-necessary cookies), parts of
          the Website may not function properly.
        </p>

        <p>
          Where we collect personal data as part of our use of cookies on the Website, we
          will do so in accordance with our{" "}
          <Link to="/privacy-policy" className="text-teal-700 underline hover:text-teal-900">
            Privacy Policy
          </Link>
          .
        </p>

        <h2>Further information</h2>
        <p>For more general guidance on cookies, see:</p>
        <ul>
          <li>
            <a href="https://ico.org.uk/for-organisations/direct-marketing-and-privacy-and-electronic-communications/guide-to-pecr/cookies-and-similar-technologies/" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
              ICO &mdash; Cookies and similar technologies <ExternalLink className="inline h-3 w-3" />
            </a>
          </li>
          <li>
            <a href="https://www.bbc.co.uk/usingthebbc/cookies/what-do-i-need-to-know-about-cookies/" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">
              BBC &mdash; What do I need to know about cookies? <ExternalLink className="inline h-3 w-3" />
            </a>
          </li>
        </ul>
      </main>
    </div>
  );
};

export default CookiePolicy;
